import React, { useState, useCallback, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { Stepper, Typography, TextField, TextDescription } from "@ann/web-components";
import { DetailsContainer, StepperContainer, DetailsContent } from "./Details.styled";
import { CREATE_ROLE_STEPS, STEP_IDS } from "../../constants/createRoleSteps";
import { createRole } from "../../../../services";
import { useToast } from "../../../../../../../components/toast";

const Details = forwardRef(({ onFormStateChange, selectedRole }, ref) => {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const isFieldEmpty = !name.trim();
  const showError = isFieldEmpty;

  // Prefill fields when selectedRole is provided
  useEffect(() => {
    if (selectedRole) {
      if (selectedRole.name) {
        setName(selectedRole.name);
      }
      if (selectedRole.description) {
        setDescription(selectedRole.description);
      }
    }
  }, [selectedRole]);

  // Update parent component when form state changes
  useEffect(() => {
    const canSubmit = name && name.trim() !== "";
    if (onFormStateChange) {
      onFormStateChange({
        canSubmit,
        isSubmitting,
      });
    }
  }, [name, isSubmitting, onFormStateChange]);

  const handleSelectStep = (stepId) => {
    switch (stepId) {
      case STEP_IDS.CREATION_TYPE:
        navigate("/v2/user-management/create-role");
        break;
      case STEP_IDS.DETAILS:
        navigate("/v2/user-management/create-role/details");
        break;
      default:
        break;
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!name || name.trim() === "") {
      setTouched(true);
      toast.error({
        message: "Role name is required",
        canClose: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Generate random UUID for code
      const code = crypto.randomUUID();

      const roleData = {
        code,
        name: name.trim(),
        description: description.trim() || "",
      };

      const response = await createRole(roleData);

      toast.success({
        message: "Role created successfully",
        canClose: true,
      });

      // Navigate to the created role details page
      // Assuming the response contains the role UUID
      const roleUuid = response?.body?.uuid || response?.body?.roleUuid;
      if (roleUuid) {
        navigate(`/v2/user-management/roles/${roleUuid}`);
      } else {
        navigate("/v2/user-management/roles");
      }
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error({
        message: error.message || "Error creating role",
        canClose: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [name, description, navigate, toast]);

  // Expose handleSubmit to parent component via ref
  useImperativeHandle(ref, () => ({
    handleSubmit,
    isSubmitting,
    canSubmit: name && name.trim() !== "",
  }));

  return (
    <DetailsContainer>
      <StepperContainer>
        <Stepper
          activeStep={STEP_IDS.DETAILS}
          onSelectStep={handleSelectStep}
          steps={CREATE_ROLE_STEPS}
        />
      </StepperContainer>
      <DetailsContent>
        <Typography category="title" size="small" weight="bold">
          Assign a role
        </Typography>
        <Typography category="body" size="medium" weight="regular">
          Assign access using predefined role templates.
        </Typography>
        <TextField
          label="Name"
          value={name}
          onChange={(value) => {
            setName(value);
            if (!touched) {
              setTouched(true);
            }
          }}
          size="small"
          supportingText={showError ? "Role name is required" : "Choose a name that clearly defines this role."}
          state={showError ? "error" : "default"}
          disabled={isSubmitting}
          required={true}
        />
        <TextDescription
          height="172px"
          label="Description"
          maxCharacters={500}
          value={description}
          onChange={setDescription}
          placeholder='Describe the purpose or responsibilities of this role. For example: "Manages sales reports and customer accounts."'
          size="small"
          disabled={isSubmitting}
        />
      </DetailsContent>
    </DetailsContainer>
  );
});

Details.displayName = "Details";

export default Details;

